import { DayTimeWindow, newWindow } from '../configStudio/configModel';
import { LimitPeriod, PickedItem } from '../wizard/wizardModel';

/** The six type tiles of 56, in drawn order. */
export type RewardType = 'text' | 'qr' | 'barcode' | 'url' | 'api' | 'pin';

export const REWARD_TYPES: RewardType[] = ['text', 'qr', 'barcode', 'url', 'api', 'pin'];

/** 57 / 69 / 70 "Code Source": fixed / generate / CSV / inventory. */
export type CodeSource = 'fixed' | 'generate' | 'csv' | 'inventory';

export const CODE_SOURCES: CodeSource[] = ['fixed', 'generate', 'csv', 'inventory'];

/** 59 "Does API require a unique code per user?" */
export type ApiCode = 'none' | 'csv' | 'inventory' | 'random';

export const API_CODES: ApiCode[] = ['none', 'csv', 'inventory', 'random'];

export type Ecc = 'L' | 'M' | 'Q' | 'H';

export type Symbology = 'code128' | 'ean13' | 'code39';

export type Visibility = 'public' | 'private' | 'system';

export type Expiry = 'never' | 'fixed' | 'days';

export interface ApiParam {
  /** The API's own body-key id; the module sends it back as `key_id`. */
  id: number | null;
  key: string;
  value: string;
  prefix: string;
  suffix: string;
}

export interface RewardLimits {
  issuanceOn: boolean;
  issuance: string;
  issuancePeriod: LimitPeriod;
  lifetimeOn: boolean;
  lifetime: string;
  perUserOn: boolean;
  perUser: string;
  perUserPeriod: LimitPeriod;
  redemptionOn: boolean;
  redemption: string;
  redemptionPeriod: LimitPeriod;
}

export type RewardStep = 'type' | 'source' | 'visibility' | 'expiry' | 'limits' | 'review';

export const REWARD_STEP_TOTAL = 5;

const ORDER: RewardStep[] = ['type', 'source', 'visibility', 'expiry', 'limits', 'review'];

export const REWARD_STEP_NUMBER: Record<RewardStep, number | null> = {
  type: 1,
  source: 2,
  visibility: 3,
  expiry: 4,
  limits: 5,
  review: null
};

export interface RewardWizardState {
  step: RewardStep;
  type: RewardType | null;
  source: CodeSource;
  codeValue: string;
  inventory: PickedItem | null;
  ecc: Ecc;
  symbology: Symbology;
  urlType: 'static' | 'unique';
  url: string;
  api: PickedItem | null;
  apiCode: ApiCode;
  params: ApiParam[];
  /** The API's query keys, sent as the module seeds them (`value: 'None'`). */
  queryParams: { id: number | null; key: string }[];
  merchant: PickedItem | null;
  visibility: Visibility;
  beginsAt: string | null;
  endsOn: boolean;
  endsAt: string | null;
  window: DayTimeWindow;
  expiry: Expiry;
  expiresAt: string | null;
  expiryDays: string;
  limits: RewardLimits;
  costCurrency: string;
  cost: string;
  sellOn: boolean;
  sellPoints: string;
  sellLoyalty: PickedItem | null;
  sellTiers: string[];
  name: string;
}

export const initialRewardState = (): RewardWizardState => ({
  step: 'type',
  type: null,
  source: 'fixed',
  codeValue: '',
  inventory: null,
  ecc: 'M',
  symbology: 'code128',
  urlType: 'static',
  url: '',
  api: null,
  apiCode: 'none',
  params: [],
  queryParams: [],
  merchant: null,
  visibility: 'public',
  beginsAt: null,
  endsOn: false,
  endsAt: null,
  window: newWindow(),
  expiry: 'never',
  expiresAt: null,
  expiryDays: '',
  limits: {
    issuanceOn: false,
    issuance: '',
    issuancePeriod: 'day',
    lifetimeOn: false,
    lifetime: '',
    perUserOn: false,
    perUser: '',
    perUserPeriod: 'day',
    redemptionOn: false,
    redemption: '',
    redemptionPeriod: 'day'
  },
  costCurrency: 'PHP',
  cost: '',
  sellOn: false,
  sellPoints: '',
  sellLoyalty: null,
  sellTiers: [],
  name: ''
});

/** A tile pick keeps the answers of the same type and moves on (56 draws no Next). */
export const chooseRewardType = (state: RewardWizardState, type: RewardType): RewardWizardState =>
  type === state.type
    ? { ...state, step: 'source' }
    : { ...initialRewardState(), name: state.name, type, step: 'source' };

export const nextRewardStep = (step: RewardStep): RewardStep =>
  ORDER[Math.min(ORDER.indexOf(step) + 1, ORDER.length - 1)];

export const previousRewardStep = (step: RewardStep): RewardStep =>
  ORDER[Math.max(ORDER.indexOf(step) - 1, 0)];

export const rewardStepAt = (number: number): RewardStep =>
  ORDER.find(step => REWARD_STEP_NUMBER[step] === number) ?? 'type';

export interface IRewardStepProps {
  state: RewardWizardState;
  update: (change: (current: RewardWizardState) => RewardWizardState) => void;
  next: () => void;
  back: () => void;
}
